/* eslint-disable max-lines */
// deps
import React, {
    ReactElement,
    memo,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useEffect,
    useRef,
    useState,
} from "react";

// internal deps
import {
    GroupOrderCartContext,
    useOnBack,
    AnalyticsContext,
    useSessionStorage,
    useToingHeaderBannerAnimation,
} from "@swiggy-private/checkout-core";
import {
    COMMON_HEADER_TEST_IDS,
    CART_HEADER_DATA_TEST_IDS,
    TOING_FLOATING_BUTTON_TEST_IDS,
} from "@swiggy-private/cart-test-ids";
import { isEmpty } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// analytics
import { clickMenuBack } from "@cart-client/analytics/events";

// context
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";

// helpers
import { getOverlayHeaderBannerData, getSavingsBannerData } from "@cart-client/helpers/Cart";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import { getAddressNudgeTooltipData } from "@cart-client/helpers/CartAddress";
import { interpolateTotalDiscountInSavingsCallout, isToingRtbXp } from "@cart-client/helpers/ToingHeader";

// style components
import {
    FloatingButtonWrap,
    HeaderBannerWrapper,
    HeaderContainer,
    ToingHeaderLottieContainer,
    ToingStatusBarStampContainer,
    ToingStatusBarStampImage,
    ToingRtbSavingsBannerSpacer,
} from "./Header.style";

// components
import HeaderContent from "./components/HeaderContent";
import AddressNudgeToolTip from "./components/AddressNudgeToolTip";
import NewSavingsBanner from "./components/NewSavingsBanner";

//hooks
import { useCartAddressNudgeAnalytics } from "@cart-client/hooks/useCartAddressNudgeAnalytics";
import useFetchAddressList from "@cart-client/hooks/useFetchAddressList";

// analytics
import {
    impressionTopCartBannerD2C,
    clickTopCartBannerD2C,
    impressionNoAddressD2C,
    clickNoAddressD2C,
} from "@cart-client/analytics/events/ToingLpg";
//constants and types
import { ADDRESS_NUDGE_TOOLTIP_STATUS } from "@swiggy-private/cart-types";
import { ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY } from "@cart-client/constants/CartAddress";
import {
    AddAddressPopup,
    getOverlayHeaderBannerAssets,
    TOING_HEADER_BANNER_ANIMATION_SEEN_SESSION_KEY,
    ToingHeaderBanner,
} from "@swiggy-private/cart-ui";
import {
    NO_ADDRESS_POPUP_TEXT,
    PRICE_COMPARISON_BANNER_CLICK_TRIGGER,
    TOING_STAMP_CONFIG,
} from "@cart-client/constants/cartHeader";
import ToingRtbHeaderLottie from "./components/ToingRtbHeaderLottie";
import { useToingRtbHeaderLottie } from "@cart-client/hooks/useToingRtbHeaderLottie";
import {
    getToingRtbHeaderLottieArray,
    getToingRtbRxAndSessionIds,
} from "@cart-client/hooks/useToingRtbHeaderLottie/helpers";

interface ToingHeaderProps {
    children: ReactNode;
    onBack?: VoidFunction;
    restaurantName?: string;
    isEmptyCart: boolean;
    isCartError?: boolean;
    isCartLoading?: boolean;
    isPageScrolled?: boolean;
    showAddressWidgetTop?: boolean;
    openHalfCard?: VoidFunction;
    isFreeCashApplicable?: boolean;
    uploadId?: string;
    onRtbHeaderAnimationPlayingChange?: (isPlaying: boolean) => void;
}

export const ACCESSIBILITY_TEXTS = {
    GO_BACK: "Go Back",
};

function ToingHeader(props: ToingHeaderProps): ReactElement {
    const {
        isEmptyCart,
        onBack: propsOnBack,
        isPageScrolled,
        showAddressWidgetTop,
        openHalfCard,
        onRtbHeaderAnimationPlayingChange,
    } = props;
    const { cartState } = useContext(CartContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { state: groupCartData } = useContext(GroupOrderCartContext);
    const {
        cartAddressState: { addressNudgeConfig },
        setCartAddressState,
    } = useContext(CartAddressContext);

    const { reviewedCart: cartResponse } = cartState;
    const isRtbXpEnabled = useMemo(() => isToingRtbXp(cartResponse), [cartResponse]);

    const {
        shouldShowLottie: shouldShowRtbHeaderLottie,
        showRtbFinalState,
        onAnimationComplete: onRtbAnimationComplete,
        isWaitingForTurn,
        isAnimationInProgress,
    } = useToingRtbHeaderLottie({ isRtbXpEnabled });

    useEffect(() => {
        onRtbHeaderAnimationPlayingChange?.(isAnimationInProgress);
        return () => onRtbHeaderAnimationPlayingChange?.(false);
    }, [isAnimationInProgress, onRtbHeaderAnimationPlayingChange]);

    const toingRtbLottieArray = useMemo(() => getToingRtbHeaderLottieArray(cartResponse), [cartResponse]);
    const { rxId, sid } = useMemo(() => getToingRtbRxAndSessionIds(cartResponse), [cartResponse]);

    const savingsBannerData = getSavingsBannerData(cartResponse);
    const overlayHeaderBannerData = getOverlayHeaderBannerData(cartResponse);
    const { rightImageAssetId } = getOverlayHeaderBannerAssets(overlayHeaderBannerData?.assets);
    const [showAddressPopup, setShowAddressPopup] = useState(false);

    const onAddressPopupImpression = useCallback(() => {
        impressionNoAddressD2C(impressionEvent, {
            text: NO_ADDRESS_POPUP_TEXT.TITLE,
            subText: NO_ADDRESS_POPUP_TEXT.SUBTEXT,
            cta: NO_ADDRESS_POPUP_TEXT.CTA,
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
        });
    }, [impressionEvent, cartState?.localCart?.cart_id, cartState?.localCart?.restaurantId]);

    const onBannerClick = useCallback(() => {
        clickTopCartBannerD2C(clickEvent, {
            text: overlayHeaderBannerData?.title ?? "",
            subText: overlayHeaderBannerData?.subtitle ?? "",
            isEligible: !isEmpty(overlayHeaderBannerData),
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            trigger: PRICE_COMPARISON_BANNER_CLICK_TRIGGER,
        });

        if (!showAddressWidgetTop) {
            setShowAddressPopup(true);
            return;
        }

        openHalfCard?.();
    }, [
        clickEvent,
        overlayHeaderBannerData,
        cartState?.localCart?.cart_id,
        cartState?.localCart?.restaurantId,
        showAddressWidgetTop,
        openHalfCard,
    ]);

    const interpolatedSavingsTitle = useMemo(
        () =>
            interpolateTotalDiscountInSavingsCallout(
                savingsBannerData?.title,
                savingsBannerData?.message_details?.total_discount,
            ),
        [savingsBannerData],
    );

    //flag to determine addressNudgeTooltip display status
    const { addressNudgeTooltipStatus } = addressNudgeConfig;

    //determine how long to show addressNudgeTooltip and get tooltip text
    const { addressNudgeToolTipDuration, addressNudgeToolTipText } = useMemo(
        () => getAddressNudgeTooltipData(addressNudgeConfig),
        [addressNudgeConfig],
    );

    const onBannerImpression = useCallback(() => {
        impressionTopCartBannerD2C(impressionEvent, {
            text: overlayHeaderBannerData?.title ?? "",
            subText: overlayHeaderBannerData?.subtitle ?? "",
            isEligible: !isEmpty(overlayHeaderBannerData),
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
        });
    }, [impressionEvent, cartState?.localCart?.cart_id, cartState?.localCart?.restaurantId, overlayHeaderBannerData]);

    const nudgeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [, setIsNudgeTooltipShownFlag] = useSessionStorage(ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY, false);

    const hideAddressNudgeTooltip = useCallback(() => {
        setCartAddressState((prevCartAddressState) => ({
            ...prevCartAddressState,
            addressNudgeConfig: {
                ...prevCartAddressState.addressNudgeConfig,
                addressNudgeTooltipStatus: ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN,
            },
        }));
    }, [setCartAddressState]);

    const handleCloseAddressNudgeToolTip = useCallback(() => {
        if (nudgeTimeoutRef?.current) {
            clearTimeout(nudgeTimeoutRef.current);
        }
        hideAddressNudgeTooltip();
    }, [hideAddressNudgeTooltip]);

    const [hasSeenAnimation, setHasSeenAnimation] = useSessionStorage(
        TOING_HEADER_BANNER_ANIMATION_SEEN_SESSION_KEY,
        false,
    );

    const shouldRegisterToingBannerAnimation = useMemo(
        () => !hasSeenAnimation && !isEmpty(overlayHeaderBannerData) && !isPageScrolled,
        [hasSeenAnimation, overlayHeaderBannerData, isPageScrolled],
    );

    const { shouldPlayAnimation, onAnimationComplete } = useToingHeaderBannerAnimation({
        shouldRegister: shouldRegisterToingBannerAnimation,
    });

    useEffect(() => {
        if (shouldPlayAnimation) {
            try {
                window.sessionStorage.setItem(TOING_HEADER_BANNER_ANIMATION_SEEN_SESSION_KEY, JSON.stringify(true));
            } catch {
                // noop
            }
        }
    }, [shouldPlayAnimation]);

    const handleToingBannerAnimationEnd = useCallback(() => {
        setHasSeenAnimation(true);
        onAnimationComplete();
    }, [setHasSeenAnimation, onAnimationComplete]);

    useEffect(() => {
        //once we can show address nudge tooltip
        if (addressNudgeTooltipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW) {
            //set key in session storage that nudge tooltip has shown
            setIsNudgeTooltipShownFlag(true);
            //set cart address state to show address nudge icon
            setCartAddressState((prevCartAddressState) => ({
                ...prevCartAddressState,
                addressNudgeConfig: {
                    ...prevCartAddressState.addressNudgeConfig,
                    showAddressNudgeIcon: true,
                },
            }));
            //set timeout to close address nudge tooltip after nudgeConfigTimout
            nudgeTimeoutRef.current = setTimeout(() => {
                hideAddressNudgeTooltip();
            }, addressNudgeToolTipDuration);
        }

        return () => {
            if (nudgeTimeoutRef?.current) {
                clearTimeout(nudgeTimeoutRef.current);
            }
        };
    }, [
        addressNudgeToolTipDuration,
        addressNudgeTooltipStatus,
        hideAddressNudgeTooltip,
        setCartAddressState,
        setIsNudgeTooltipShownFlag,
    ]);

    const onBack = useOnBack();

    const onBackButtonClick = useCallback(() => {
        clickMenuBack(clickEvent, cartState?.localCart?.cart_id?.toString(), groupCartData?.data?.group_cart_id, {
            isEmptyCart,
        });

        updateStatusBarCleanup();

        if (typeof propsOnBack === "function") {
            propsOnBack();
            return;
        }
        onBack();
    }, [
        cartState?.localCart?.cart_id,
        clickEvent,
        groupCartData?.data?.group_cart_id,
        onBack,
        propsOnBack,
        isEmptyCart,
    ]);

    const { onImpressionCheckoutLatLng } = useCartAddressNudgeAnalytics();
    const { onAddOrSelectAddressClick } = useFetchAddressList();

    useEffect(() => {
        onImpressionCheckoutLatLng();
    }, [onImpressionCheckoutLatLng]);

    const hasOverlayHeaderBanner = !isEmpty(overlayHeaderBannerData);
    const showNewSavingsBanner = !isEmpty(savingsBannerData) && (!hasOverlayHeaderBanner || Boolean(isPageScrolled));
    const showStatusBarStamp = !props.isCartLoading && !props.isCartError;

    const onAddressSelectCtaClick = useCallback(() => {
        clickNoAddressD2C(clickEvent, {
            text: NO_ADDRESS_POPUP_TEXT.TITLE,
            subText: NO_ADDRESS_POPUP_TEXT.SUBTEXT,
            cta: NO_ADDRESS_POPUP_TEXT.CTA,
            cartId: cartState?.localCart?.cart_id?.toString(),
            restaurantId: String(cartState?.localCart?.restaurantId ?? ""),
            ctaType: NO_ADDRESS_POPUP_TEXT.CTA,
        });
        setShowAddressPopup(false);
        onAddOrSelectAddressClick();
    }, [onAddOrSelectAddressClick, cartState?.localCart?.cart_id, cartState?.localCart?.restaurantId, clickEvent]);

    return (
        <>
            <HeaderContainer data-testid={COMMON_HEADER_TEST_IDS.CART_HEADER} $isRtbXpEnabled={isRtbXpEnabled}>
                {shouldShowRtbHeaderLottie && (
                    <ToingHeaderLottieContainer data-testid={COMMON_HEADER_TEST_IDS.TOING_RTB_HEADER_LOTTIE}>
                        <ToingRtbHeaderLottie
                            key={`${isWaitingForTurn ? "waiting" : "main"}-${rxId}-${sid}`}
                            lottieArray={toingRtbLottieArray}
                            showFinalState={
                                isWaitingForTurn || (!shouldPlayAnimation && !isWaitingForTurn && showRtbFinalState)
                            }
                            onPlayComplete={onRtbAnimationComplete}
                        />
                    </ToingHeaderLottieContainer>
                )}
                {showStatusBarStamp && !props.isEmptyCart && !isRtbXpEnabled && (
                    <ToingStatusBarStampContainer data-testid={CART_HEADER_DATA_TEST_IDS.TOING_STAMP}>
                        <ToingStatusBarStampImage
                            cloudinaryId={TOING_STAMP_CONFIG.imageAssetId}
                            customBackground={{ light: "", dark: "" }}
                        />
                    </ToingStatusBarStampContainer>
                )}
                <HeaderContent
                    onBackButtonClick={onBackButtonClick}
                    restaurantName={props.restaurantName}
                    isCartError={props.isCartError}
                    isEmptyCart={props.isEmptyCart}
                    isRtbXpEnabled={isRtbXpEnabled}
                    isAnimationInProgress={isAnimationInProgress}
                >
                    <>{props.children}</>
                </HeaderContent>
                {isRtbXpEnabled ? (
                    <ToingRtbSavingsBannerSpacer data-testid="toing-rtb-savings-spacer" />
                ) : (
                    showNewSavingsBanner && <NewSavingsBanner title={interpolatedSavingsTitle} />
                )}
                {!isRtbXpEnabled && !isEmpty(overlayHeaderBannerData) && (
                    <HeaderBannerWrapper $isHidden={Boolean(isPageScrolled)}>
                        <ToingHeaderBanner
                            overlayHeaderBanner={overlayHeaderBannerData}
                            savingsCallout={interpolatedSavingsTitle}
                            shouldAnimate={shouldPlayAnimation}
                            setHasSeenAnimation={handleToingBannerAnimationEnd}
                            onImpression={onBannerImpression}
                            onBannerClick={onBannerClick}
                        />
                    </HeaderBannerWrapper>
                )}
            </HeaderContainer>
            {showStatusBarStamp && (
                <AddressNudgeToolTip
                    addressNudgeToolTipText={addressNudgeToolTipText}
                    addressNudgeToolTipStatus={addressNudgeTooltipStatus}
                    onCloseClick={handleCloseAddressNudgeToolTip}
                />
            )}
            {!isPageScrolled && !isEmpty(overlayHeaderBannerData) && !isEmpty(rightImageAssetId) && (
                <FloatingButtonWrap
                    $animate={shouldPlayAnimation}
                    onClick={onBannerClick}
                    data-testid={TOING_FLOATING_BUTTON_TEST_IDS.WRAPPER}
                >
                    <Image cloudinaryId={rightImageAssetId} width="116px" height="126px" />
                </FloatingButtonWrap>
            )}
            {showAddressPopup && (
                <AddAddressPopup
                    closeModal={() => setShowAddressPopup(false)}
                    onCtaClick={onAddressSelectCtaClick}
                    onImpression={onAddressPopupImpression}
                />
            )}
        </>
    );
}

export default memo(ToingHeader);
